import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Divider } from "@mui/material"
import { useSnackbar } from "notistack"
import React from "react"
import { ApiError } from "../../Api/ApiError"
import { deleteEquipmentCancelConf, deleteStudioCancelConf, getCancelConfList, regEquipmentCancelConf, regStudioCancelConf } from "../../Api/System"
import { EquipmentCancelConf } from "../../Objects/EquipmentCancelConf"
import { StudioCancelConf } from "../../Objects/StudioCancelConf"
import { useAbortController } from "../../hooks/useAbortController"
import StudioCancelConfList from "../molecules/StudioCancelConfList"
import EquipmentCancelConfList from "../molecules/EquipmentCancelConfList"

export interface CancelPolicyDialogProps {
    open: boolean
    onClose: (event: {}) => void
}
const CancelPolicyDialog: React.FC<CancelPolicyDialogProps> = ({
    open,
    onClose,
}) => {
    const { enqueueSnackbar } = useSnackbar()
    const { getSignal } = useAbortController()

    const [studioCancelConfList, setStudioCancelConfList] = React.useState<StudioCancelConf[]>()
    const [equipmentCancelConfList, setEquipmentCancelConfList] = React.useState<EquipmentCancelConf[]>()

    const fetchData = React.useCallback(() => {
        getCancelConfList({
            signal: getSignal()
        }).then((result) => {
            setStudioCancelConfList(result.studioCancelConfList)
            setEquipmentCancelConfList(result.equipmentCancelConfList)
        }).catch((e: any) => {
            if (e.__CANCEL__) {
                console.info("canceled")
            } else if (e instanceof ApiError) {
                enqueueSnackbar(e.message, { variant: "error" })
            } else if (e instanceof Error) {
                enqueueSnackbar(e.message, { variant: "error" })
            } else {
                enqueueSnackbar("通信に失敗しました", { variant: "error" })
            }
        })
    }, [enqueueSnackbar, getSignal])

    const doDeleteStudioCancelConf = React.useCallback((studioCancelConf: StudioCancelConf) => {
        deleteStudioCancelConf(studioCancelConf, {
            signal: getSignal()
        }).then(() => {
            fetchData()
        }).catch((e: any) => {
            if (e.__CANCEL__) {
                console.info("canceled")
            } else if (e instanceof ApiError) {
                enqueueSnackbar(e.message, { variant: "error" })
            } else if (e instanceof Error) {
                enqueueSnackbar(e.message, { variant: "error" })
            } else {
                enqueueSnackbar("通信に失敗しました", { variant: "error" })
            }
        })
    }, [enqueueSnackbar, getSignal, fetchData])

    const doCreateStudioCancelConf = React.useCallback((studioCancelConf: StudioCancelConf) => {
        regStudioCancelConf(studioCancelConf, {
            signal: getSignal()
        }).then(() => {
            fetchData()
            enqueueSnackbar("登録しました", { variant: "success" })
        }).catch((e: any) => {
            if (e.__CANCEL__) {
                console.info("canceled")
            } else if (e instanceof ApiError) {
                enqueueSnackbar(e.message, { variant: "error" })
            } else if (e instanceof Error) {
                enqueueSnackbar(e.message, { variant: "error" })
            } else {
                enqueueSnackbar("通信に失敗しました", { variant: "error" })
            }
        })
    }, [enqueueSnackbar, getSignal, fetchData])

    const doDeleteEquipmentCancelConf = React.useCallback((equipmentCancelConf: EquipmentCancelConf) => {
        deleteEquipmentCancelConf(equipmentCancelConf, {
            signal: getSignal()
        }).then(() => {
            fetchData()
        }).catch((e: any) => {
            if (e.__CANCEL__) {
                console.info("canceled")
            } else if (e instanceof ApiError) {
                enqueueSnackbar(e.message, { variant: "error" })
            } else if (e instanceof Error) {
                enqueueSnackbar(e.message, { variant: "error" })
            } else {
                enqueueSnackbar("通信に失敗しました", { variant: "error" })
            }
        })
    }, [enqueueSnackbar, getSignal, fetchData])

    const doCreateEquipmentCancelConf = React.useCallback((equipmentCancelConf: EquipmentCancelConf) => {
        regEquipmentCancelConf(equipmentCancelConf, {
            signal: getSignal()
        }).then(() => {
            fetchData()
            enqueueSnackbar("登録しました", { variant: "success" })
        }).catch((e: any) => {
            if (e.__CANCEL__) {
                console.info("canceled")
            } else if (e instanceof ApiError) {
                enqueueSnackbar(e.message, { variant: "error" })
            } else if (e instanceof Error) {
                enqueueSnackbar(e.message, { variant: "error" })
            } else {
                enqueueSnackbar("通信に失敗しました", { variant: "error" })
            }
        })
    }, [enqueueSnackbar, getSignal, fetchData])

    React.useEffect(() => {
        if (open) {
            fetchData()
        }
    }, [open, fetchData])

    return (
        <Dialog
            open={open}
            onClose={onClose}
            fullWidth
        >
            <DialogTitle>キャンセルポリシー設定</DialogTitle>
            <DialogContent>
                <StudioCancelConfList
                    studioCancelConfList={studioCancelConfList ?? []}
                    onDelete={doDeleteStudioCancelConf}
                    onCreate={doCreateStudioCancelConf}
                />
                <Divider
                    sx={{
                        my: 2,
                    }}
                />
                <EquipmentCancelConfList
                    equipmentCancelConfList={equipmentCancelConfList ?? []}
                    onDelete={doDeleteEquipmentCancelConf}
                    onCreate={doCreateEquipmentCancelConf}
                />
            </DialogContent>
            <DialogActions>
                <Button
                    onClick={onClose}
                >閉じる</Button>
            </DialogActions>
        </Dialog>
    )
}

export default CancelPolicyDialog